/** @module Components */

import React from 'react'
import { ScheduleDay } from '../ConsensusEvent/components/ScheduleDay'
import { SponsorsSection } from '../ConsensusEvent/components/SponsorsSection'
import { ScheduleNavigation } from '../ConsensusEvent/components/ScheduleNavigation'
import { ShuttleSchedule } from '../ConsensusEvent/components/ShuttleSchedule'
import {
  SundaySchedule,
  MondaySchedule,
  FridaySchedule
} from '../ConsensusEvent/components/SimpleSchedules'
import { ScheduleDayProps } from '../ConsensusEvent/types'

export interface EventData {
  title: string
  subtitle: string
  description: React.ReactNode
  registrationLink?: string
  shuttleSchedule?: boolean
  sponsors?: boolean
  schedules: {
    sunday?: boolean
    monday?: boolean
    tuesday?: ScheduleDayProps
    wednesday?: ScheduleDayProps
    thursday?: ScheduleDayProps
    friday?: boolean
  }
  disclosures?: {
    disclosureLink?: string
    cmeLink?: string
  }
}

interface EventPageProps {
  data: EventData
}

export const EventPage: React.FC<EventPageProps> = ({ data }) => {
  const handleScrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId)
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }

  return (
    <main className="min-h-screen bg-[#EDE6DE] px-4 py-12 md:px-8 lg:px-16">
      <div className="max-w-7xl mx-auto">
        <h1 className="text-4xl md:text-5xl font-serif text-[#3C2416] mb-6">
          {data.title}
        </h1>
        <h2 className="text-2xl md:text-3xl font-serif text-[#A6410A] mb-8">
          {data.subtitle}
        </h2>

        <ScheduleNavigation onNavigate={handleScrollToSection} />

        <div className="prose prose-lg max-w-none mb-8 md:mb-12">
          <div className="text-[#3C2416] text-base md:text-lg leading-relaxed">
            {data.description}
          </div>
        </div>

        {data.shuttleSchedule && <ShuttleSchedule />}

        {data.sponsors && <SponsorsSection />}

        <div className="grid gap-8">
          {data.schedules.sunday && <SundaySchedule />}
          {data.schedules.monday && <MondaySchedule />}
          {data.schedules.tuesday && (
            <ScheduleDay id="tuesday-schedule" {...data.schedules.tuesday} />
          )}
          {data.schedules.wednesday && (
            <ScheduleDay
              id="wednesday-schedule"
              {...data.schedules.wednesday}
            />
          )}
          {data.schedules.thursday && (
            <ScheduleDay id="thursday-schedule" {...data.schedules.thursday} />
          )}
          {data.schedules.friday && <FridaySchedule />}
        </div>

        {data.disclosures && (
          <div className="mt-12 bg-white rounded-lg shadow-md p-6 md:p-8">
            <h2 className="text-2xl md:text-3xl font-serif text-[#3C2416] mb-6">
              Disclosures & CME Information
            </h2>
            <div className="space-y-4">
              {data.disclosures.disclosureLink && (
                <p className="text-[#3C2416] text-base md:text-lg">
                  <a
                    href={data.disclosures.disclosureLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#142841] underline hover:no-underline font-medium"
                  >
                    Click here for disclosure information
                  </a>
                </p>
              )}
              {data.disclosures.cmeLink && (
                <p className="text-[#3C2416] text-base md:text-lg">
                  <a
                    href={data.disclosures.cmeLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#142841] underline hover:no-underline font-medium"
                  >
                    Click here for CME credit information
                  </a>
                </p>
              )}
            </div>
          </div>
        )}
      </div>
    </main>
  )
}
